import { CODE128_QUIET_ZONE_MODULES, code128Bars, isEncodableInCode128B } from '@sitzmark/core';
import type { CSSProperties } from 'react';

/**
 * A real Code 128 barcode, drawn as SVG rectangles (SCOPE §7, decision 29).
 *
 * **Not a styled div, and not a font.** Chrome's print dialog defaults
 * "Background graphics" to **off**, so bars painted with `background: #000`
 * never reach the paper — the label prints with a blank strip where the barcode
 * should be, and it is discovered one item at a time at the register weeks
 * later. An SVG `rect` is content rather than decoration, so it prints
 * regardless of that setting.
 *
 * A barcode font has the same shape of problem one step further along: it
 * prints only if the font is installed on the machine doing the printing, and
 * when it is not the receipt shows the number wrapped in asterisks and nobody
 * notices it is unscannable until a customer is standing there with a return.
 * That is exactly what the receipt did until this component reached it.
 *
 * The `viewBox` is in **barcode modules** and the SVG scales to whatever box it
 * is given, so bar ratios stay exact at any size — a distorted barcode is an
 * unscannable one, and `preserveAspectRatio="none"` is deliberate: stretching
 * the *height* is free, and the width is already exact because the viewBox is
 * the module count.
 *
 * **The quiet zone is inside the viewBox**, ten modules either side, because it
 * is specified in modules and a module is however wide the code left room for.
 * Leaving it to the label's padding meant a fixed 0.1in supplied ten modules of
 * silence on a long code and under five on a short one — and a scanner that
 * cannot find the start of a symbol does not beep, which reads as a bad label, a
 * bad scanner or a bad item, and never as a layout bug. In the box it scales
 * with the bars and is right by construction.
 *
 * Shared rather than copied. It was written for labels and the receipt needed
 * the same thing; a second copy is how two renderings of one idea drift, which
 * this codebase has now paid for with `.chip`, `.toggle`, `.matrix` and
 * `TENDER_LABELS`.
 */
export function Barcode({
  value,
  className,
  style,
  height = 100,
}: {
  value: string;
  className?: string;
  /** A size worked out from the code itself — the count sheet sets its width per SKU. */
  style?: CSSProperties;
  /** viewBox height in modules. Only the ratio matters; CSS sets the real size. */
  height?: number;
}) {
  // Code 128-B covers the printable ASCII these codes are drawn from. Anything
  // outside it is not encodable, and a barcode that cannot be scanned is worse
  // than none — the number below it is always readable either way.
  if (!isEncodableInCode128B(value)) return null;

  const { bars, width } = code128Bars(value);
  const quiet = CODE128_QUIET_ZONE_MODULES;

  return (
    <svg
      className={className}
      style={style}
      viewBox={`${-quiet} 0 ${width + 2 * quiet} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Barcode ${value}`}
    >
      {bars.map((bar, index) => (
        <rect key={index} x={bar.x} y={0} width={bar.width} height={height} fill="#000" />
      ))}
    </svg>
  );
}
